import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import api from "utils/api";
import { useAuth } from "context/AuthContext";
import Pagination from "components/pagination";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";

// Helper component for details
const Detail = ({ label, value }) => (
  <div className="flex justify-between border-b py-1 text-sm">
    <span className="font-medium">{label}</span>
    <span>{value}</span>
  </div>
);

// Modal wrapper
const Modal = ({ title, children, onClose }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-500/50">
    <div className="w-[95%] max-w-md rounded-xl bg-white p-4 sm:w-full sm:p-6">
      <h3 className="mb-4 text-lg font-bold">{title}</h3>
      {children}
    </div>
  </div>
);

export default function UsersTable() {
  const navigate = useNavigate();
  const { userId } = useAuth();
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    mobile_no: "",
    password: "",
    role: "",
    status: 1,
    user_id: null,
  });

  const [editIndex, setEditIndex] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const isEdit = editIndex != null;

  const USERS_PAGE_SIZE = 10;
  const [usersPage, setUsersPage] = useState(1);
  const filteredUsers = (users || []).filter((user) => {
    const term = searchTerm.toLowerCase();
    return (
      user.name?.toLowerCase().includes(term) ||
      user.email?.toLowerCase().includes(term) ||
      (user.mobile_no + "").includes(term) ||
      user.role?.toLowerCase().includes(term) ||
      (user.status === 1 ? "active" : "inactive").includes(term)
    );
  });

  const usersTotalPages = Math.max(
    1,
    Math.ceil(filteredUsers.length / USERS_PAGE_SIZE)
  );

  const paginatedUsers = filteredUsers.slice(
    (usersPage - 1) * USERS_PAGE_SIZE,
    usersPage * USERS_PAGE_SIZE
  );

  // Load users from backend
  const loadUsers = async () => {
    try {
      const res = await api.post("/list_user", { role: "A" });
      setUsers(res.data || []);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load users");
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  // Reset form
  const resetForm = () => {
    setFormData({
      name: "",
      email: "",
      mobile_no: "",
      password: "",
      role: "A",
      status: 1,
      user_id: null,
    });
    setEditIndex(null);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Submit form (create or edit)
  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (isEdit) {
        await api.post("/edit_user", {
          ...formData,
          admin_id: formData.user_id,
        });
        toast.success("User updated successfully");
      } else {
        await api.post("/create_user", {
          name: formData.name,
          email: formData.email,
          mobile_no: Number(formData.mobile_no),
          password: formData.password,
          role: formData.role,
          user_id: userId,
        });
        toast.success("User created successfully");
      }
      resetForm();
      setShowModal(false);
      loadUsers();
    } catch (err) {
      console.error(err);
      const d = err.response?.data?.detail;
      const msg =
        typeof d === "string"
          ? d
          : Array.isArray(d)
          ? d.map((x) => x.msg).join(" ")
          : d?.msg;
      toast.error(msg || "Failed to submit");
    }
  };

  const handleEdit = (user) => {
    setFormData({
      name: user.name,
      email: user.email,
      mobile_no: user.mobile_no,
      role: user.role,
      password: "",
      status: user.status,
      user_id: user.id,
    });
    setEditIndex(user.id);
    setShowModal(true);
  };

  const handleView = (user) => {
    setSelectedUser(user);
  };

  const handleChangeStatus = async (user, status) => {
    try {
      await api.post("/edit_user_status", {
        user_id: user.id,
        status,
        admin_id: userId,
      });
      toast.success("User status updated");
      loadUsers();
    } catch (err) {
      console.error(err);
      toast.error("Failed to update status");
    }
  };

  return (
    <div className="space-y-6 p-4 sm:p-6">
      {/* Header */}
      <div className="rounded-xl bg-white p-6 shadow-md transition-shadow duration-300 hover:shadow-xl">
        <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          {/* Left - Title */}
          <h4 className="text-xl font-bold text-navy-700 dark:text-white">
            Users
          </h4>
          {/* Right - Add Button + Search */}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="relative w-full sm:w-64">
              <Search
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              />

              <input
                type="text"
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setUsersPage(1);
                }}
                className="w-full rounded-lg border px-3 py-2 pl-9 text-sm focus:outline-none focus:ring-2 focus:ring-brand-300"
              />
            </div>

            <button
              type="button"
              onClick={() => {
                resetForm();
                setShowModal(true);
              }}
              className="flex items-center gap-2 whitespace-nowrap rounded-lg bg-brand-500 px-4 py-2 text-white"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"></path>
                <circle cx="12" cy="7" r="4"></circle>
              </svg>
              <span>Add Admin</span>
            </button>
          </div>
        </div>
        {/* Table */}
        <div className="w-full overflow-x-auto">
          <table className="w-full min-w-[700px] border-collapse text-sm">
            <thead>
              <tr className="border-b text-center">
                <th className="py-5">Name</th>
                <th className="py-5">Email</th>
                <th className="py-5">Phone</th>
                <th className="py-2">Role</th>
                <th className="py-2">Status</th>
                <th className="py-2">Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan="6" className="py-4 text-center text-gray-400">
                    No users added
                  </td>
                </tr>
              ) : (
                paginatedUsers.map((user, index) => (
                  <tr
                    key={user.id || index}
                    className="border-b text-center hover:bg-gray-50"
                  >
                    <td className="py-5">{user.name}</td>
                    <td className="py-2">{user.email}</td>
                    <td className="py-2">{user.mobile_no}</td>
                    <td className="py-2">
                      {user.role === "A" ? "Admin" : user.role}
                    </td>

                    <td
                      className={`py-2 capitalize ${
                        user.status === 1
                          ? "font-semibold text-green-600"
                          : "font-semibold text-red-600"
                      }`}
                    >
                      {user.status === 1 ? "Active" : "Inactive"}
                    </td>
                    <td className="py-2">
                      <div className="flex flex-wrap justify-center gap-2">
                        <button
                          onClick={() =>
                            navigate(`/admin/user_details/${user.id}`)
                          }
                          className="rounded-md bg-gray-200 px-3 py-1 text-xs font-medium text-gray-700 hover:bg-gray-300"
                        >
                          View
                        </button>
                        <button
                          onClick={() =>
                            navigate(`/admin/edit_user/${user.id}`)
                          }
                          className="rounded-md bg-indigo-500 px-3 py-1 text-xs font-medium text-white transition hover:bg-indigo-600"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() =>
                            handleChangeStatus(user, user.status === 1 ? 2 : 1)
                          }
                          className={`rounded-md px-3 py-1 text-xs font-medium ${
                            user.status === 1
                              ? "bg-red-500 text-white"
                              : "bg-green-500 text-white"
                          } transition hover:opacity-80`}
                        >
                          {user.status === 1 ? "Deactivate" : "Activate"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <Pagination
          currentPage={usersPage}
          totalPages={usersTotalPages}
          totalItems={filteredUsers.length || 0}
          pageSize={USERS_PAGE_SIZE}
          onPageChange={setUsersPage}
          itemName="users"
        />

        {/* Add/Edit Modal */}
        {showModal && (
          <Modal
            title={isEdit ? "Edit User" : "Add Admin"}
            onClose={() => setShowModal(false)}
          >
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div>
                  <label className="mb-2 block text-xs font-semibold uppercase text-gray-900">
                    Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Enter your name"
                    className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-gray-700
              transition focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-400"
                    required
                  />
                </div>
                <div>
                  <label className="mb-2 block text-xs font-semibold uppercase text-gray-900">
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Enter your email"
                    className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-gray-700
              transition focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-400"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="mb-2 block text-xs font-semibold uppercase text-gray-900">
                  Password
                </label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Create Password"
                  className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-gray-700
              transition focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-400"
                  required={!isEdit}
                />
              </div>
              <div>
                <label className="mb-2 block text-xs font-semibold uppercase text-gray-900">
                  Phone
                </label>
                <input
                  type="text"
                  name="mobile_no"
                  value={formData.mobile_no}
                  onChange={handleChange}
                  placeholder="Enter your Mobile no"
                  className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-gray-700
              transition focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <input type="hidden" name="role" value="A"></input>
                </div>
              </div>
              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    resetForm();
                    setShowModal(false);
                  }}
                  className="rounded-lg border px-4 py-2"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="rounded-lg bg-brand-500 px-4 py-2
            text-sm font-medium text-white shadow-sm transition hover:scale-[1.02]"
                >
                  {isEdit ? "Update User" : "Create User"}
                </button>
              </div>
            </form>
          </Modal>
        )}
      </div>
    </div>
  );
}
